// The update gate's decisions (shell/UpdateGate.tsx runs them): when the installed app looks for a
// new version behind its loading screen, and whether a version it found is taken into use now.
// Pure functions; the service worker and the screen live in update-gate.ts and UpdateGate.tsx.
//
// The rules (docs/DEPLOY.md "How the phones update"):
//   - cold start of an installed app (a service worker already controls the page), and coming
//     back after at least 30 minutes away: check, at most CHECK_BUDGET_MS, then go on with what
//     is cached. Offline: no check at all, the app starts at once. A first visit has nothing to
//     update.
//   - never in the middle of something: on a return during a study session, with grades not yet
//     written to the device, or on a screen with typed input or a recording, the check waits
//     until that is over (deferred).
//   - a found version is installed and the page reloads once into it; the reload marker stops a
//     loop when the reload did not bring a new build.
import type { SyncState } from '../lib/sync';

/**
 * The check never holds the loading screen longer than this. 3.5 s was too short on a phone's 4G
 * (the new version arrived only at the next open, 2026-10-06); a found version is worth the wait.
 */
export const CHECK_BUDGET_MS = 8000;
/**
 * On a cold start a version found but still downloading after CHECK_BUDGET_MS is waited for up to
 * this long behind the loading screen, then taken into use at once (Axel 2026-10-21: no waiting
 * for an idle moment at launch). Only at launch: no study session or typed input can be running yet.
 */
export const START_INSTALL_MS = 120_000;
/** Away at least this long counts as opening the app again. */
export const AWAY_MS = 30 * 60_000;
/** A reload for an update that did not change the build within this time is not repeated. */
export const LOOP_WINDOW_MS = 2 * 60_000;

/** Screens without typed input or recording, where a reload loses nothing. */
export const QUIET_PATHS = ['/', '/lessons', '/dictionary', '/grammar', '/listen', '/more', '/install'] as const;

export function quietPath(path: string): boolean {
  return QUIET_PATHS.some((p) => (p === '/' ? path === '/' : path === p || path.startsWith(`${p}/`)));
}

export type GateTrigger = 'start' | 'resume';
export type GateDecision = 'check' | 'skip' | 'defer';

export interface GateContext {
  trigger: GateTrigger;
  /** A service worker controls the page (the app was installed before). */
  hasController: boolean;
  online: boolean;
  /** How long the app was hidden (resume only). */
  hiddenForMs: number;
  inSession: boolean;
  /** Grades not yet written to IndexedDB. */
  unsaved: number;
  syncState: SyncState;
  path: string;
}

export function gateDecision(c: GateContext): GateDecision {
  if (!c.hasController || !c.online) return 'skip';
  if (c.trigger === 'resume' && c.hiddenForMs < AWAY_MS) return 'skip';
  if (c.inSession || c.unsaved > 0 || c.syncState === 'syncing' || (c.trigger === 'resume' && !quietPath(c.path))) return 'defer';
  return 'check';
}

/** What the service worker check found. */
export type CheckResult = 'none' | 'waiting' | 'timeout' | 'offline' | 'error';

/** Written just before reloading into a new version (sessionStorage). */
export interface ReloadMarker {
  build: string;
  at: number;
}

export function afterCheck(result: CheckResult, marker: ReloadMarker | null, build: string, now: number): 'apply' | 'continue' {
  if (result !== 'waiting') return 'continue';
  // We reloaded for an update moments ago and still run the same build: the new version did not
  // take over (e.g. another tab holds the old one). Go on instead of reloading again.
  if (marker && marker.build === build && now - marker.at < LOOP_WINDOW_MS) return 'continue';
  return 'apply';
}

/** On start: a marker from another build means the update arrived; it is cleared either way once old. */
export function markerOnStart(marker: ReloadMarker | null, build: string, now: number): { keep: boolean; updated: boolean } {
  if (!marker) return { keep: false, updated: false };
  if (marker.build !== build) return { keep: false, updated: true };
  return { keep: now - marker.at < LOOP_WINDOW_MS, updated: false };
}
